import * as vp from 'vopay';
import { Box, Chip, Table, Card, TextField, Icon, withStyles, MaterialSymbol } from 'vopay';
import { Button, DropdownMenu, Title } from 'UI';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import useFetchUsers from '@/hooks/useApi/useFetchUsers';
import useDeleteUser from '@/hooks/useApi/useDeleteUser';
import { useTranslation } from 'react-i18next';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useDialog } from '@/providers/Dialog.provider';
import { useToast } from '@/providers/Toast.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import RolesSummaryCard from './UserRoles/RolesSummaryCard';
import useComputePartnerRoleCounts from './UserRoles/useComputePartnerRoleCounts';
import useUserRoleLabels from '@/hooks/useUserRoleLabels';
import ManageRoleDialog from './ManageRole/ManageRoleDialog';
import ChangePasswordDialog from './ChangePassword/ChangePasswordDialog';
import CreateUserDrawer from './CreateUser/CreateUserDrawer';
import styles from './UsersList.module.scss';

const cn = withStyles(styles);

type FC = FunctionComponent;

const UsersList: FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateUserDrawerOpen, setIsCreateUserDrawerOpen] = useState(false);
  const { deleteUser } = useDeleteUser();
  const roleLabels = useUserRoleLabels();
  const [users, fetchUsers] = useFetchUsers({});
  const { t } = useTranslation('translations', { keyPrefix: 'Users.List' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t: tDeleteUser } = useTranslation('translations', { keyPrefix: 'Users.List.DeleteUser' });
  const dialog = useDialog();
  const toast = useToast();
  const alertDialog = useAlertDialog();

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  useEffect(() => {
    fetchUsers();
  }, []);

  const allUsers = useMemo(() => Object.values(users.Users || {}), [users]);

  const filteredUsers = useMemo(
    () =>
      allUsers.filter((user) => searchTerm === '' || user.Username.toLowerCase().includes(searchTerm.toLowerCase())),
    [allUsers, searchTerm],
  );

  const roleCounts = useComputePartnerRoleCounts(allUsers);

  const handleManageRoleDialog = async (username: string, currentRole: string) => {
    const result = await dialog.open<boolean>(({ onConfirm, onCancel }) => (
      <ManageRoleDialog onConfirm={onConfirm} onCancel={onCancel} emailAddress={username} currentRole={currentRole} />
    ));

    if (result === null) return;

    if (result) {
      toast.success(t('ManageRoleDialog.success', { defaultValue: 'The role was updated successfully.' }));
      fetchUsers();
    } else {
      toast.neutral(t('ManageRoleDialog.error', { defaultValue: 'Unable to update the role.' }));
    }
  };

  const handleChangePasswordDialog = async (username: string) => {
    const result = await dialog.open<boolean>(({ onConfirm, onCancel }) => (
      <ChangePasswordDialog onConfirm={onConfirm} onCancel={onCancel} emailAddress={username} />
    ));

    if (result === null) return;

    if (result) {
      toast.success(t('ChangePasswordDialog.success', { defaultValue: 'The password was updated successfully.' }));
    } else {
      toast.neutral(t('ChangePasswordDialog.error', { defaultValue: 'Unable to update the password.' }));
    }
  };

  const handleDeleteUser = async (username: string) => {
    const result = await alertDialog.caution(
      tDeleteUser('Confirmation.title', { defaultValue: 'Are you sure you want to delete the user?' }),
      tDeleteUser('Confirmation.subTitle', { defaultValue: 'This action cannot be undone.' }),
      {
        abortLabel: tDeleteUser('Confirmation.cancelLabel', { defaultValue: 'No' }),
        confirmLabel: tDeleteUser('Confirmation.confirmLabel', { defaultValue: 'Yes' }),
      },
    );

    if (result !== 'confirm') return;

    try {
      await deleteUser({ EmailAddress: username });
      toast.success(tDeleteUser('Success.title', { defaultValue: 'Success! The user was successfully deleted.' }));
      fetchUsers();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      alertDialog.error(
        tDeleteUser('Error.title', { defaultValue: 'Error! The user was not deleted.' }),
        tDeleteUser('Error.subTitle', { defaultValue: '{{errorMessage}}', errorMessage }),
      );
    }
  };

  const tableProps: vp.TableProps<{
    Username: string;
    UserType: string;
    DateAdded: string;
  }> = {
    id: 'users-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'Username',
        header: t('Table.username'),
        align: 'left',
        field: 'Username',
      },
      {
        id: 'DateAdded',
        header: t('Table.dateAdded'),
        align: 'left',
        field: 'DateAdded',
      },
      {
        id: 'UserType',
        header: t('Table.role', { defaultValue: 'Role' }),
        align: 'left',
        template: ({ data }) => <Chip label={roleLabels[data.UserType] ?? data.UserType} />,
      },
      {
        id: 'view',
        align: 'right',
        header: '',
        template: ({ data }) => (
          <Box jc="fe" gap="sm">
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button size="small" colorScheme="secondary" iconRight="keyboard_arrow_down" disabled={isRoleViewOnly}>
                  {tGlobal('actions')}
                </Button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Content align="end">
                {/* TODO: label is singular ("Manage Role") and the target dialog is a
                    single-role Select, because partner users hold exactly one UserType.
                    Account Portal's equivalent is "Manage Role(s)" backed by a multi-select
                    (SelectRoleDialogue, many RoleIDs per user). When ID-13192 shares this
                    component, this needs a real branch on portal type for both the copy
                    and the dialog implementation — not just a label swap. */}
                <DropdownMenu.Item
                  onSelect={() => handleManageRoleDialog(data.Username, data.UserType)}
                  left={<MaterialSymbol icon="groups_2" size={18} />}
                >
                  {t('Table.manageRoleButton', { defaultValue: 'Manage Role' })}
                </DropdownMenu.Item>

                <DropdownMenu.Item
                  onSelect={() => handleChangePasswordDialog(data.Username)}
                  left={<MaterialSymbol icon="lock" size={18} />}
                >
                  {t('Table.changePasswordButton')}
                </DropdownMenu.Item>

                <DropdownMenu.Item
                  onSelect={() => handleDeleteUser(data.Username)}
                  colorScheme="destructive"
                  left={<MaterialSymbol icon="close" size={18} color="#e8160b" />}
                >
                  {t('Table.removeButton')}
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          </Box>
        ),
      },
    ],
    rows: filteredUsers.map((user) => ({
      id: user.Username,
      data: user,
    })),
  };

  return (
    <div>
      <Box mb="md" jc="sb" ai="center">
        <Title level="1">{t('pageTitle', { defaultValue: 'User Management' })}</Title>

        <div className={cn('searchArea')}>
          <TextField
            key="UserSearchKeyword"
            name="UserSearchKeyword"
            placeholder={t('Search.placeholder', { defaultValue: 'Search user' })}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            variant="outlineShadow"
            elementLeft={
              <div className={cn('searchIcon')}>
                <Icon icon="search" width="17px" height="17px" color="none" />
              </div>
            }
          />

          <Button
            id="showHideAccountCredentials"
            colorScheme="primary"
            size="small"
            iconLeft="add"
            disabled={isRoleViewOnly}
            onClick={() => setIsCreateUserDrawerOpen(true)}
          >
            {t('addUserButton', { defaultValue: 'Add User' })}
          </Button>
        </div>
      </Box>

      <RolesSummaryCard roles={roleCounts} />

      <Card
        style={{
          padding: '16px',
        }}
      >
        <Table {...tableProps} />
      </Card>

      <CreateUserDrawer
        isOpen={isCreateUserDrawerOpen}
        existingUsernames={allUsers.map((user) => user.Username)}
        onClose={() => setIsCreateUserDrawerOpen(false)}
        onUserCreated={fetchUsers}
      />
    </div>
  );
};

export default UsersList;
